/**
 * Lead Management Enums
 *
 * Enums for lead/prospect tracking in CRM and pipeline features.
 */

import { z } from 'zod';

// ============================================================================
// LEAD STATUS
// ============================================================================

/**
 * Lead lifecycle status
 */
export enum LeadStatus {
  NEW = 'new',
  CONTACTED = 'contacted',
  QUALIFIED = 'qualified',
  NURTURING = 'nurturing',
  CONVERTED = 'converted',
  LOST = 'lost',
  ARCHIVED = 'archived',
}

/** Zod schema for LeadStatus validation */
export const LeadStatusSchema = z.nativeEnum(LeadStatus);

/** Array of all lead statuses for iteration */
export const LEAD_STATUSES = Object.values(LeadStatus);

// ============================================================================
// LEAD SOURCE
// ============================================================================

/**
 * Lead acquisition source/channel
 */
export enum LeadSource {
  WEBSITE = 'website',
  REFERRAL = 'referral',
  COLD_CALL = 'cold_call',
  EMAIL = 'email',
  SOCIAL = 'social',
  EVENT = 'event',
  PARTNER = 'partner',
  OTHER = 'other',
}

/** Zod schema for LeadSource validation */
export const LeadSourceSchema = z.nativeEnum(LeadSource);

/** Array of all lead sources for iteration */
export const LEAD_SOURCES = Object.values(LeadSource);

// ============================================================================
// LEAD TEMPERATURE
// ============================================================================

/**
 * Lead engagement/interest level
 */
export enum LeadTemperature {
  COLD = 'cold',
  WARM = 'warm',
  HOT = 'hot',
}

/** Zod schema for LeadTemperature validation */
export const LeadTemperatureSchema = z.nativeEnum(LeadTemperature);

/** Array of all lead temperatures for iteration */
export const LEAD_TEMPERATURES = Object.values(LeadTemperature);

// ============================================================================
// ENRICHMENT STATUS (Issue #525)
// ============================================================================

/**
 * Status of lead data enrichment from external sources
 */
export enum EnrichmentStatus {
  NONE = 'none',
  PENDING = 'pending',
  COMPLETED = 'completed',
  FAILED = 'failed',
  PARTIAL = 'partial',
}

/** Zod schema for EnrichmentStatus validation */
export const EnrichmentStatusSchema = z.nativeEnum(EnrichmentStatus);

/** Array of all enrichment statuses for iteration */
export const ENRICHMENT_STATUSES = Object.values(EnrichmentStatus);

// ============================================================================
// QUALIFICATION STATUS (Issue #525)
// ============================================================================

/**
 * Lead qualification status from scoring/review
 */
export enum QualificationStatus {
  UNQUALIFIED = 'unqualified',
  MARKETING_QUALIFIED = 'marketing_qualified',
  SALES_QUALIFIED = 'sales_qualified',
  PRODUCT_QUALIFIED = 'product_qualified',
  DISQUALIFIED = 'disqualified',
}

/** Zod schema for QualificationStatus validation */
export const QualificationStatusSchema = z.nativeEnum(QualificationStatus);

/** Array of all qualification statuses for iteration */
export const QUALIFICATION_STATUSES = Object.values(QualificationStatus);

// ============================================================================
// CONTACT TYPES (Issue #525 - Multi-Value Contacts)
// ============================================================================

/**
 * Email contact type
 */
export enum EmailContactType {
  WORK = 'work',
  PERSONAL = 'personal',
  OTHER = 'other',
}

/** Zod schema for EmailContactType validation */
export const EmailContactTypeSchema = z.nativeEnum(EmailContactType);

/**
 * Phone contact type
 */
export enum PhoneContactType {
  MOBILE = 'mobile',
  WORK = 'work',
  HOME = 'home',
  FAX = 'fax',
  OTHER = 'other',
}

/** Zod schema for PhoneContactType validation */
export const PhoneContactTypeSchema = z.nativeEnum(PhoneContactType);

/**
 * Address contact type
 */
export enum AddressContactType {
  MAILING = 'mailing',
  PROPERTY = 'property',
  OFFICE = 'office',
  OTHER = 'other',
}

/** Zod schema for AddressContactType validation */
export const AddressContactTypeSchema = z.nativeEnum(AddressContactType);
